import loadable from '@loadable/component'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react'
import {Navigate, Routes, useLocation, useNavigate} from 'react-router-dom'
import {NoAccess, NotFound} from './components/errors'
import {LinkButton} from './components/library/buttons'
import {NoResults} from './components/library/list'
import {Loader} from './components/loader'
import {
  PermissionAction,
  PermissionResource
} from './data/workbench-api/generated/types'
import {useAnalytics} from './lib/analytics/analytics'
import {getLocalEnvironmentId, setLocalEnvironmentId} from './lib/local-store'
import {PrivateRoute, Route} from './lib/router'
import {OrganisationNode} from './organisation'
import {AfBulkSend} from './views/af-bulk-send'
import {CancelCards} from './views/cancel-cards'
import {Tools} from './views/tools'

const AnalyticsDownload = loadable(
  async () => (await import('./views/analytics-download')).AnalyticsDownload,
  {
    fallback: <Loader />
  }
)

const Analytics = loadable(
  async () => (await import('./views/analytics')).Analytics,
  {
    fallback: <Loader />
  }
)

const WebhookRequestLog = loadable(
  async () => (await import('./views/webhooks/request-log')).WebhookRequestLog,
  {
    fallback: <Loader />
  }
)

const BatchSend = loadable(
  async () => (await import('./views/batch-send')).BatchSend,
  {
    fallback: <Loader />
  }
)

const ValidateToken = loadable(
  async () => (await import('./views/validate-token')).ValidateToken,
  {
    fallback: <Loader />
  }
)

const CardEditorContainer = loadable(
  async () => (await import('./views/card-builder')).CardEditorContainer,
  {
    fallback: <Loader />
  }
)

const CardTemplateList = loadable(
  async () => (await import('./views/card-list')).CardTemplateList,
  {
    fallback: <Loader />
  }
)
const Dashboard = loadable(
  async () => (await import('./views/dashboard')).Dashboard,
  {
    fallback: <Loader />
  }
)
const DashboardV2 = loadable(
  async () => (await import('./views/dashboard-v2')).Dashboard,
  {
    fallback: <Loader />
  }
)

const MediaList = loadable(
  async () => (await import('./views/media')).MediaList,
  {
    fallback: <Loader />
  }
)

const MediaListV2 = loadable(
  async () => (await import('./views/media/media-library')).MediaLibrary,
  {
    fallback: <Loader />
  }
)

const Users = loadable(async () => (await import('./views/end-users')).Users, {
  fallback: <Loader />
})

const Customers = loadable(
  async () => (await import('./views/customers')).Customers,
  {
    fallback: <Loader />
  }
)

const Configuration = loadable(
  async () => (await import('./views/configuration')).Configuration,
  {
    fallback: <Loader />
  }
)

const EditTheme = loadable(
  async () =>
    (await import('./views/configuration/container-themes/editor')).EditTheme,
  {
    fallback: <Loader />
  }
)

const ActionFlows = loadable(
  async () => (await import('./views/action-flows/index')).Index,
  {
    fallback: <Loader />
  }
)

const ActionFlow = loadable(
  async () => (await import('./views/action-flow/index')).Index,
  {
    fallback: <Loader />
  }
)

const CustomerCsvImporter = loadable(
  async () =>
    (await import('./views/customer-csv-importer/index')).CustomerCsvImporter,
  {
    fallback: <Loader />
  }
)
export interface EnvironmentNode {
  id: string
  name: string

  // TODO consider removal, use current org's versions directly instead?
  organisationId: string
  organisationName: string

  hasPermission: (
    resource: PermissionResource,
    action: PermissionAction
  ) => boolean
}

interface RouteRedirectProps {
  uri?: string
  path?: string
}
interface EnvironmentRedirectProps extends RouteRedirectProps {
  organisation: OrganisationNode
  loaded: boolean
  subPath?: string
}

export const EnvironmentRedirect = ({
  organisation,
  loaded,
  subPath
}: EnvironmentRedirectProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const environments = organisation.environments

  useEffect(() => {
    if (loaded) {
      const lastEnvironmentId = getLocalEnvironmentId(organisation.id)
      // only use the value in localStorage if its valid for the current user,
      // otherwise fall back to the first available environment
      const redirectId =
        lastEnvironmentId &&
        environments.some(env => env.id.toString() === lastEnvironmentId)
          ? lastEnvironmentId
          : environments[0]?.id

      if (redirectId) {
        const to = subPath
          ? `/${organisation.id}/${redirectId}/${subPath}${location.search}`
          : redirectId
        navigate(to, {replace: true})
      }
    }
  }, [
    environments,
    loaded,
    location.search,
    navigate,
    organisation.id,
    subPath
  ])

  if (loaded && !environments.length) {
    const canCreateEnvironment = organisation?.hasPermission(
      PermissionResource.Environment,
      PermissionAction.Edit
    )

    return (
      <NoAccess>
        <NoResults heading="No active environments" icon="plug" message={''}>
          {canCreateEnvironment ? (
            <>
              <p>Create or restore an environment to continue</p>
              <LinkButton to="environments" appearance="transparent">
                Manage environments
              </LinkButton>
            </>
          ) : (
            <p>Please contact your organisation administrator</p>
          )}
        </NoResults>
      </NoAccess>
    )
  }

  return <Loader />
}

interface EnvironmentProps {
  environmentId: string
  loaded: boolean
}

interface EnvironmentContextType {
  organisation: OrganisationNode | undefined
  environment: EnvironmentNode | undefined
  environments: EnvironmentNode[] // TODO remove, this is contained in OrganisationNode
  setEnvironmentId(environmentId?: string): void
  switcherMode: 'environment' | 'organisation'
  setSwitcherMode: (mode: 'environment' | 'organisation') => void
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(
  undefined
)

export const useEnvironment = () => {
  const ctx = useContext(EnvironmentContext)
  if (!ctx) {
    throw new Error('Calling useEnvironment outside environment provider')
  }
  return ctx
}

interface EnvironmentProviderProps {
  organisation?: OrganisationNode
  render: (context: EnvironmentContextType) => React.ReactNode
}

export const EnvironmentProvider = ({
  organisation,
  render
}: EnvironmentProviderProps) => {
  const [environmentId, setEnvironmentIdState] = useState<string | undefined>()
  const [switcherMode, setSwitcherMode] = useState<
    'environment' | 'organisation'
  >('organisation')

  const analytics = useAnalytics()

  useEffect(() => {
    setEnvironmentIdState(
      organisation?.id ? getLocalEnvironmentId(organisation?.id) : undefined
    )
  }, [organisation?.id])

  const setEnvironmentId = useCallback(
    (id?: string) => {
      setEnvironmentIdState(id)

      // analytics
      const previousWorkingEnv =
        organisation?.id && getLocalEnvironmentId(organisation.id)
      if (previousWorkingEnv && previousWorkingEnv !== id) {
        analytics.track({
          event: 'Switched environment',
          properties: {
            org_id: organisation.id,
            environment_id: id,
            category: 'general'
          }
        })
      }

      // save last rendered environment in localStorage
      if (organisation?.id) setLocalEnvironmentId(organisation.id, id || '')
    },
    [organisation?.id, analytics]
  )

  const value = useMemo(() => {
    const environments = organisation?.environments ?? []

    return {
      organisation: organisation,
      environments,

      // It's useful to have a sensible default selected when we don't have an id available.
      environment: environmentId
        ? environments.find(en => en.id === environmentId) ?? environments[0]
        : environments[0],
      setEnvironmentId,
      switcherMode,
      setSwitcherMode
    }
  }, [switcherMode, environmentId, organisation, setEnvironmentId])
  return (
    <EnvironmentContext.Provider value={value}>
      {render(value)}
    </EnvironmentContext.Provider>
  )
}

export const Environment = ({environmentId, loaded}: EnvironmentProps) => {
  const {setEnvironmentId, organisation, setSwitcherMode} = useEnvironment()

  const environment = organisation?.environments.find(
    e => e.id === environmentId
  )

  useLayoutEffect(() => {
    setEnvironmentId(environmentId)

    // only show environment switcher while in this route
    setSwitcherMode('environment')
    return () => setSwitcherMode('organisation')
  }, [setEnvironmentId, environmentId, setSwitcherMode])

  if (!loaded) {
    return null
  }

  if (!environment || environmentId !== environment.id) {
    // If the environmentId in the URL doesn't match render not found here
    return <NotFound />
  }

  const preferredLandingPage =
    organisation?.currentBusinessUser?.preferences.landingPage ?? 'cards'
  const landingPage = !organisation?.legacyCardsEnabled
    ? 'action-flows'
    : preferredLandingPage

  return (
    <Routes>
      <Route path="/" render={() => <Navigate to={landingPage} replace />} />

      <Route // basic AF json editor ( as well as list view )
        path="/action-flows/*"
        render={() => (
          <ActionFlows environment={environment} organisation={organisation} />
        )}
      />

      <Route // New AF editor
        path="/action-flow/*"
        render={() => <ActionFlow environment={environment} />}
      />

      <PrivateRoute
        path="/dashboard"
        environment={environment}
        render={() => <Dashboard environment={environment} />}
      />

      <PrivateRoute
        path="/dashboard-v2"
        environment={environment}
        render={() => <DashboardV2 environment={environment} />}
      />

      <Route
        path="/cards/*"
        render={() => <CardRoutes environment={environment} />}
      />

      <Route
        path="/media"
        render={() => (
          <MediaList environment={environment} organisation={organisation} />
        )}
      />

      <Route
        path="/media-v2"
        render={() => <MediaListV2 environment={environment} />}
      />

      <Route
        path="/analytics"
        render={() => <Analytics environment={environment} />}
      />

      <Route
        path="/webhook-requests"
        render={() => <WebhookRequestLog environment={environment} />}
      />

      <Route
        path="/analytics-exporter"
        render={() => <AnalyticsDownload environment={environment} />}
      />

      <Route
        path="/customer-csv-importer"
        render={() => <CustomerCsvImporter environment={environment} />}
      />

      <Route
        path="/cancel-cards"
        render={() => <CancelCards environment={environment} />}
      />

      <Route
        path="/af-bulk-send"
        render={() => <AfBulkSend environment={environment} />}
      />

      <Route
        path="/settings/*"
        render={() => (
          <Navigate
            to={`/${environment.organisationId}/${environment.id}/configuration`}
            replace={true}
          />
        )}
      />

      <Route
        path="/configuration/*"
        render={() => <Configuration environment={environment} />}
      />

      <Route
        path="/themes"
        render={() => (
          <Navigate
            to={`/${environment.organisationId}/${environment.id}/configuration/container-themes`}
            replace={true}
          />
        )}
      />

      <Route
        path="/themes/:id"
        render={({id}) => <EditTheme id={id} environment={environment} />}
      />

      <Route
        path="/testing-tools"
        render={() => (
          <Navigate
            to={`/${environment.organisationId}/${environment.id}/testing-tools/batch-send`}
            replace={true}
          />
        )}
      />
      <Route
        path="/testing-tools/batch-send"
        render={() => <BatchSend environment={environment} />}
      />
      <Route
        path="/testing-tools/validate-auth-token"
        render={() => <ValidateToken environment={environment} />}
      />
      {/* Redirect for BC (/users -> /customers) */}
      <Route
        path="/users"
        render={() => (
          <Navigate
            to={`/${environment.organisationId}/${environment.id}/customers`}
            replace={true}
          />
        )}
      />
      {/* Redirect for BC (/customers-v2 -> /customers) */}
      <Route
        path="/customers-v2"
        render={() => (
          <Navigate
            to={`/${environment.organisationId}/${environment.id}/customers`}
            replace={true}
          />
        )}
      />
      <Route
        path="/customers"
        render={() => <Customers environment={environment} />}
      />
      <Route
        path="/customers-v1"
        render={() => <Users environment={environment} />}
      />

      <Route
        path="/tools/"
        render={() => <Tools environment={environment} />}
      />

      <Route path="*" render={() => <NotFound />} />
    </Routes>
  )
}

interface EnvironmentRoutesProps {
  environment: EnvironmentNode
}

// encapsulating in a Router so internal Routes can be relative
// TODO check if this is actually necessary? Maybe they're relative anyway?
const CardRoutes = ({environment}: EnvironmentRoutesProps) => (
  <Routes>
    <Route
      path="/"
      render={() => <CardTemplateList environment={environment} />}
    />

    <Route
      path="/:id/*"
      render={({id}) => (
        <CardEditorContainer id={id} environment={environment} />
      )}
    />
  </Routes>
)
